import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './AllClasses.css';
import { getApiUrl, API_ENDPOINTS, getAuthHeaders } from '../../../config/api.config';
import { toggleEnvironment } from '../../../utils/apiClient';
import refreshToken from '../../../utils/apiUtils';
import { useTrialContext } from '../../../contexts/TrialContext';

const AllClasses = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, classId: null, className: '' });
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const { checkLimit, trialStatus } = useTrialContext();
  
  // Get user account type from localStorage
  const userAccount = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null;
  const isTrialAccount = userAccount?.accountType === 'trial' || trialStatus?.isTrial;
  // Only show warning if this is a trial/free account AND has reached the limit (2 classes for free plan)
  const isClassLimitReached = isTrialAccount && classes.length >= 2;

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await axios.get(
          getApiUrl(API_ENDPOINTS.TEACHER.CLASSES),
          { headers: getAuthHeaders() }
        );
        setClasses(response.data.data || []);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching classes:", err);
        setError("Failed to load classes");
        setLoading(false);
      }
    };

    fetchClasses();
  }, []);

  const makeDeleteRequest = async (classId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No token available');
    }

    try {
      const response = await axios.delete(
        `/api/v1/stem@home/classes/${classId}`,
        {
          baseURL: toggleEnvironment(),
          headers: {
            ...getAuthHeaders(),
            'Authorization': `Bearer ${token}`
          }
        }
      );
      return response;
    } catch (err) {
      if (err.response?.status === 401) {
        const newToken = await refreshToken();
        if (newToken) {
          const retryResponse = await axios.delete(
            `/api/v1/stem@home/classes/${classId}`,
            {
              baseURL: toggleEnvironment(),
              headers: {
                ...getAuthHeaders(),
                'Authorization': `Bearer ${newToken}`
              }
            }
          );
          return retryResponse;
        }
        throw new Error('Token refresh failed');
      }
      throw err;
    }
  };

  const handleDeleteClass = async (classId) => {
    try {
      setDeleting(true);
      console.log('Deleting class:', classId);
      
      const response = await makeDeleteRequest(classId);

      if (response.data.status === 'success') {
        // Remove the class from local state
        setClasses(prevClasses => prevClasses.filter(c => c._id !== classId));
        setDeleteModal({ isOpen: false, classId: null, className: '' });
        console.log('Class deleted successfully');
      } else {
        throw new Error(response.data.message || 'Failed to delete class');
      }
    } catch (err) {
      console.error("Error deleting class:", err);
      alert(err.response?.data?.message || 'Failed to delete class');
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteModal = (classId, className, event) => {
    event.stopPropagation(); // Prevent navigating to class details
    setDeleteModal({ isOpen: true, classId, className });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, classId: null, className: '' });
  };

  if (loading) {
    return (
      <div className="container">
        <div className="header">
          <h1>Classes</h1>
          <button 
            className="add-class-button"
            onClick={() => navigate('/create-class')}
          >
            + Add New Class
          </button>
        </div>
        <div className="loading">Loading classes...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="header">
          <h1>Classes</h1>
          <button 
            className="add-class-button"
            onClick={() => navigate('/create-class')}
          >
            + Add New Class
          </button>
        </div>
        <div className="error-state">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Free plan limit warning banner */}
      {isClassLimitReached && (
        <div className="limit-warning" style={{ 
          backgroundColor: '#dc3545', 
          color: 'white', 
          padding: '15px', 
          margin: '15px 0', 
          borderRadius: '4px', 
          fontWeight: 'bold', 
          textAlign: 'center' 
        }}>
          ⚠️ <strong>FREE PLAN LIMIT REACHED:</strong> You cannot create more than 2 classes with your free account
        </div>
      )}
      
      <div className="header">
        <h1>Classes ({classes.length})</h1>
        <button 
          className={`add-class-button ${isClassLimitReached ? 'disabled' : ''}`}
          onClick={() => !isClassLimitReached && navigate('/create-class')}
          disabled={isClassLimitReached}
          style={isClassLimitReached ? { 
            opacity: 0.5, 
            cursor: 'not-allowed',
            backgroundColor: '#a0a0a0'
          } : {}}
        >
          + Add New Class
        </button>
      </div>

      {classes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📚</div>
          <div className="empty-state-text">You haven't created any classes yet</div>
          {!isClassLimitReached && (
            <button 
              className="add-class-button"
              onClick={() => navigate('/create-class')}
            >
              Create Your First Class
            </button>
          )}
        </div>
      ) : (
        <div className="classes-grid">
          {classes.map((classItem) => (
            <div 
              key={classItem._id} 
              className="class-card"
              onClick={() => navigate(`/class/${classItem._id}`)}
            >
              <h2 className="class-name">{classItem.name}</h2>
              <div className="class-stats">
                <div className="stat-item">
                  <div className="stat-label">Students</div>
                  <div className="stat-value">{classItem.students?.length || 0}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Questions</div>
                  <div className="stat-value">{classItem.questions?.length || 0}</div>
                </div>
              </div>
              <div className="card-actions">
                <button 
                  className="delete-button"
                  onClick={(e) => openDeleteModal(classItem._id, classItem.name, e)}
                  title="Delete class"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Delete Class</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the class <strong>"{deleteModal.className}"</strong>?</p>
              <p>This action cannot be undone and will remove all associated questions and submissions.</p>
            </div>
            <div className="modal-actions">
              <button 
                className="cancel-button"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-button"
                onClick={() => handleDeleteClass(deleteModal.classId)}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Class'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllClasses; 